import useTopics from "@features/community-v2/hooks/useTopics";
import { ChevronDownIcon } from "@heroicons/react/solid";
import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import slugify from "slugify";

export default function CommunityNavLinks({
  isAuthenticated,
}: {
  isAuthenticated?: boolean;
}): JSX.Element {
  const { data: topics } = useTopics();
  const [openMenu, setOpenMenu] = useState<"subtypes" | "communityFilters">(
    null
  );
  const { asPath } = useRouter();

  const subtypesWithSlugs = useMemo(
    () =>
      topics?.subtypes.map((topic) => ({
        ...topic,
        slug: slugify(topic.title, { lower: true }),
      })),
    [topics]
  );

  const communityFiltersWithSlugs = useMemo(
    () =>
      topics?.communityFilters.map((topic) => ({
        ...topic,
        slug: slugify(topic.title, { lower: true }),
      })),
    [topics]
  );

  return (
    <ul className="space-y-2 mt-2">
      <li className="py-1">
        <Link href="/community/posts" passHref>
          <a className="flex items-center gap-1 font-bold">
            <span>
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.125 18.375V11.375C13.125 11.1429 13.0328 10.9204 12.8687 10.7563C12.7046 10.5922 12.4821 10.5 12.25 10.5H8.75C8.51794 10.5 8.29538 10.5922 8.13128 10.7563C7.96719 10.9204 7.875 11.1429 7.875 11.375V18.375"
                  stroke="black"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.625 8.75C2.62494 8.49543 2.68042 8.24392 2.78756 8.013C2.89471 7.78208 3.05095 7.57732 3.24538 7.413L9.37037 2.163C9.68624 1.89605 10.0864 1.74958 10.5 1.74958C10.9136 1.74958 11.3138 1.89605 11.6296 2.163L17.7546 7.413C17.9491 7.57732 18.1053 7.78208 18.2124 8.013C18.3196 8.24392 18.3751 8.49543 18.375 8.75V16.625C18.375 17.0891 18.1906 17.5342 17.8624 17.8624C17.5342 18.1906 17.0891 18.375 16.625 18.375H4.375C3.91087 18.375 3.46575 18.1906 3.13756 17.8624C2.80937 17.5342 2.625 17.0891 2.625 16.625V8.75Z"
                  stroke="black"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span>All Posts</span>
          </a>
        </Link>
      </li>

      {isAuthenticated ? (
        <li className="py-1">
          <Link href="/community/favorites" passHref>
            <a className="flex items-center gap-1 font-bold">
              <span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8334 17.5L10.0001 14.1667L4.16675 17.5V4.16667C4.16675 3.72464 4.34234 3.30072 4.6549 2.98816C4.96746 2.67559 5.39139 2.5 5.83341 2.5H14.1667C14.6088 2.5 15.0327 2.67559 15.3453 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                    stroke="black"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span>Saved Posts</span>
            </a>
          </Link>
        </li>
      ) : null}

      {subtypesWithSlugs?.length > 0 && (
        <li className="py-1 font-normal">
          <button
            type="button"
            onClick={() =>
              setOpenMenu((prev) => (prev === "subtypes" ? null : "subtypes"))
            }
            className="font-bold w-full flex justify-between items-center"
          >
            <span>OCD Subtypes</span>

            <ChevronDownIcon
              className={cn(
                "w-4 h-4 text-indigo-600",
                openMenu === "subtypes" && "rotate-180"
              )}
            />
          </button>

          {openMenu === "subtypes" && (
            <div className="mt-2">
              <ul>
                {subtypesWithSlugs.map((topic) => {
                  const isActive = asPath.includes(topic.slug);

                  return (
                    <li
                      key={topic.id}
                      className={cn(isActive && "text-indigo-600", "px-2 py-2")}
                    >
                      <Link
                        href={
                          isActive
                            ? "/community/posts"
                            : `/community/topics/${topic.slug}`
                        }
                        passHref
                      >
                        <a>{topic.title}</a>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </li>
      )}

      {communityFiltersWithSlugs?.length > 0 && (
        <li className="py-1 font-normal">
          <button
            type="button"
            onClick={() =>
              setOpenMenu((prev) =>
                prev === "communityFilters" ? null : "communityFilters"
              )
            }
            className="font-bold w-full flex justify-between items-center"
          >
            <span>Community Filters</span>

            <ChevronDownIcon
              className={cn(
                "w-4 h-4 text-indigo-600",
                openMenu === "communityFilters" && "rotate-180"
              )}
            />
          </button>

          {openMenu === "communityFilters" && (
            <div className="mt-2">
              <ul>
                {communityFiltersWithSlugs.map((topic) => {
                  const isActive = asPath.includes(topic.slug);

                  return (
                    <li
                      key={topic.id}
                      className={cn(isActive && "text-indigo-600", "px-2 py-2")}
                    >
                      <Link
                        href={
                          isActive
                            ? "/community/posts"
                            : `/community/topics/${topic.slug}`
                        }
                        passHref
                      >
                        <a>{topic.title}</a>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </li>
      )}
    </ul>
  );
}
